/**
 * useFeedbackToggle.ts — 8209-B
 *
 * Custom hook encapsulating the toggle/open logic for the Feedback panel.
 * Extracts duplicated inline handlers from Menu/index.js into a single
 * reusable hook so that both render paths (hasAccessToFeatures and fallback)
 * share the same behavior.
 *
 * Rules:
 * - handleFeedbackClick: toggles chapterEditorActive.
 *   When ACTIVATING: closes linking menu, deactivates prose generator.
 *   When DEACTIVATING: only sets chapterEditorActive to false.
 * - handleChevronClick: opens the panel if not already open (never closes).
 *   When ACTIVATING: closes linking menu, deactivates prose generator.
 *   When already open: no-op.
 */
import { useState, useRef, useLayoutEffect } from "react";
import { useStoryBibleStore } from "../stores/storyBibleStore";
import { useProjectStore } from "../stores/projectStore";
import { useFeedbackCreditEstimate, getFeedbackEstimateErrorMessage, FeedbackQuote, FeedbackRequest } from "./useFeedbackCreditEstimate";
import { useToast } from "../components/ToastWrapper";
import { getProjectFeedbackCategoryState } from "../components/TiptapDocumentSB/feedbackCategorySelection";
import { CREDIT_WARNING_THRESHOLD } from "../shared/feedbackCreditEstimate";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UseFeedbackToggleReturn {
  chapterEditorActive: boolean;
  selectedFeedbackCategory: string | null;
  hasUserSelectedFeedbackCategory: boolean;
  handleFeedbackClick: () => Promise<void>;
  handleChevronClick: () => void;
  showCreditWarning: boolean;
  confirmFeedback: () => void;
  dismissCreditWarning: () => void;
  estimatedCredits: number;
  isEstimatingFeedback: boolean;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useFeedbackToggle(project?: { id?: string | null; data?: { feedbackPreferences?: unknown } } | string | null, documentId?: string): UseFeedbackToggleReturn {
  const projectId = typeof project === "string" ? project : project?.id;
  const projectFeedbackPreferences = typeof project === "string" ? undefined : project?.data?.feedbackPreferences;
  const chapterEditorActive = useStoryBibleStore((state) => state.chapterEditorActive);
  const selectedFeedbackCategoryGlobal = useStoryBibleStore((state) => state.selectedFeedbackCategory);
  const hasUserSelectedFeedbackCategoryGlobal = useStoryBibleStore((state) => state.hasUserSelectedFeedbackCategory);
  const feedbackCategoryByProjectId = useStoryBibleStore((state) => state.feedbackCategoryByProjectId);
  const setChapterEditorActive = useStoryBibleStore((state) => state.setChapterEditorActive);
  const setProseGeneratorActive = useStoryBibleStore((state) => state.setProseGeneratorActive);
  const triggerFeedback = useStoryBibleStore((state) => state.triggerFeedback);
  const setShowLinkingMenu = useProjectStore((state) => state.setShowLinkingMenu);

  const [showCreditWarning, setShowCreditWarning] = useState(false);
  const [warningCredits, setWarningCredits] = useState(0);
  const { selectedFeedbackCategory, hasUserSelectedFeedbackCategory } = getProjectFeedbackCategoryState({
    projectId,
    projectFeedbackPreferences,
    feedbackCategoryByProjectId,
    selectedFeedbackCategory: selectedFeedbackCategoryGlobal,
    hasUserSelectedFeedbackCategory: hasUserSelectedFeedbackCategoryGlobal,
  });

  const estimate = useFeedbackCreditEstimate({ documentId, project, selectedFeedbackCategory });
  const { setToast } = useToast();
  const pendingQuote = useRef<FeedbackQuote | null>(null);
  const busy = useRef<{ revision: number } | null>(null);
  const currentKey = useRef(estimate.requestKey);
  currentKey.current = estimate.requestKey;
  useLayoutEffect(() => {
    // A retry may already belong to the new revision before this render commits.
    // Only retire stale attempts, rather than clearing every attempt on render.
    if (chapterEditorActive || (busy.current && !estimate.isRevisionCurrent(busy.current.revision))) busy.current = null;
    if (chapterEditorActive || !pendingQuote.current || !estimate.isQuoteCurrent(pendingQuote.current)) {
      pendingQuote.current = null;
      setShowCreditWarning(false);
    }
  }, [estimate.requestKey, estimate.validityRevision, chapterEditorActive]);
  useLayoutEffect(() => () => {
    busy.current = null;
    pendingQuote.current = null;
  }, []);

  const activateFeedback = (request: FeedbackRequest) => {
    setShowLinkingMenu(false);
    setProseGeneratorActive(false);
    setChapterEditorActive(true);
    triggerFeedback("button", request);
  };

  const handleFeedbackClick = async () => {
    if (chapterEditorActive) {
      setChapterEditorActive(false);
      return;
    }
    // Report this synchronously: loading may finish before an async error returns.
    if (estimate.settingsUnavailableMessage) {
      setToast(estimate.settingsUnavailableMessage);
      return;
    }
    if (busy.current && estimate.isRevisionCurrent(busy.current.revision)) return;
    if (pendingQuote.current && estimate.isQuoteCurrent(pendingQuote.current)) return;
    pendingQuote.current = null;
    const attempt = { revision: estimate.getValidityRevision() };
    busy.current = attempt;
    const isCurrentAttempt = () => busy.current === attempt && estimate.isRevisionCurrent(attempt.revision);
    try {
      const quote = await estimate.getFreshQuote();
      if (!quote || !isCurrentAttempt() || !estimate.isQuoteCurrent(quote)) return;
      if (!quote.estimate.isValid) throw new Error("Invalid Feedback estimate");
      if (quote.estimate.totalCredits > CREDIT_WARNING_THRESHOLD) {
        pendingQuote.current = quote;
        setWarningCredits(quote.estimate.totalCredits);
        setShowCreditWarning(true);
      } else {
        activateFeedback(quote.request);
      }
    } catch (error) {
      if (isCurrentAttempt()) setToast(getFeedbackEstimateErrorMessage(error));
    } finally {
      if (busy.current === attempt) busy.current = null;
    }
  };

  const confirmFeedback = () => {
    const quote = pendingQuote.current;
    pendingQuote.current = null;
    setShowCreditWarning(false);
    if (!quote || quote.requestKey !== currentKey.current || !estimate.isQuoteCurrent(quote)) return;
    activateFeedback(quote.request);
  };

  const dismissCreditWarning = () => {
    pendingQuote.current = null;
    setShowCreditWarning(false);
  };

  const handleChevronClick = () => {
    setShowLinkingMenu(false);
    setProseGeneratorActive(false);
  };

  return {
    chapterEditorActive,
    selectedFeedbackCategory,
    hasUserSelectedFeedbackCategory,
    handleFeedbackClick,
    handleChevronClick,
    showCreditWarning,
    confirmFeedback,
    dismissCreditWarning,
    estimatedCredits: showCreditWarning ? warningCredits : estimate.totalCredits,
    isEstimatingFeedback: estimate.isLoading,
  };
}

export default useFeedbackToggle;
